/* Incoterms® 2020 — the eleven rules, as published by the ICC.
 *
 * A typed field became a picker because the three letters are the whole meaning
 * and a typo in them is not a typo: "CFR" and "CIF" differ by who insures the
 * cargo, and "DAP" against "DDP" decides who pays the import duty. Free text let
 * a quote go out saying "CIP" when the deal was CIF, and the offer PDF carries
 * that to the client as a term of the contract.
 *
 * It is also the same substitution FR-39 makes for client names: the source
 * workbook joined on strings somebody retyped, and the same fact arrived in
 * several spellings — "FOB", "F.O.B.", "fob Guangzhou".
 *
 * The 2020 edition, not 2010. DAT was renamed DPU in 2020 and FCA gained the
 * on-board bill of lading option; a picker offering DAT would be offering a rule
 * that no longer exists.
 *
 * ORDER is the ICC's own, not alphabetical: the four E/F/C/D groups run from
 * least seller obligation to most, which is the axis a trader actually compares
 * along. Alphabetising them would scatter that.
 *
 * The list is a CLIENT-SIDE narrowing. The server stores the incoterm as bounded
 * short text (quote/domain.go) and will accept anything within that length, so
 * an older quote holding a term not on this list — or one written with its named
 * place, "DAP Ashgabat", as the seed data has it — still reads back and still
 * prints. Nothing here rejects stored data; it only shapes what is offered next.
 */

export interface Incoterm {
  code: string
  /** The rule's full name, as the ICC writes it. */
  name: string
  /** Which of the four ICC groups it belongs to. */
  group: 'E' | 'F' | 'C' | 'D'
}

export const INCOTERMS_2020: readonly Incoterm[] = [
  // E — the seller hands over at their own premises.
  { code: 'EXW', name: 'Ex Works', group: 'E' },

  // F — the seller delivers to a carrier the BUYER nominates; main carriage
  //     is the buyer's to arrange and pay for.
  { code: 'FCA', name: 'Free Carrier', group: 'F' },
  { code: 'FAS', name: 'Free Alongside Ship', group: 'F' },
  { code: 'FOB', name: 'Free On Board', group: 'F' },

  // C — the seller pays the main carriage but risk still passes early, which is
  //     the trap in this group: paid-for is not the same as at-risk-for.
  { code: 'CFR', name: 'Cost and Freight', group: 'C' },
  { code: 'CIF', name: 'Cost, Insurance and Freight', group: 'C' },
  { code: 'CPT', name: 'Carriage Paid To', group: 'C' },
  { code: 'CIP', name: 'Carriage and Insurance Paid To', group: 'C' },

  // D — the seller carries risk all the way to the named destination.
  { code: 'DAP', name: 'Delivered at Place', group: 'D' },
  { code: 'DPU', name: 'Delivered at Place Unloaded', group: 'D' },
  { code: 'DDP', name: 'Delivered Duty Paid', group: 'D' },
]

/** The four rules that only apply to sea and inland waterway carriage.
 *
 * Kept as data rather than as a comment because it is the most common
 * misapplication of the set: FOB and CIF are written on air waybills and road
 * consignments constantly, where they have no defined meaning. ASA's lanes are
 * mostly rail and road, so the picker says which four are the sea ones. */
export const SEA_ONLY_INCOTERMS: readonly string[] = ['FAS', 'FOB', 'CFR', 'CIF']

/** The picker's options: the code as the value, its full name beneath.
 *
 * The name is a `hint` rather than part of the label because the CODE is what
 * goes on the offer and what a trader recognises — "Free On Board" is the gloss,
 * not the term. Both are searchable either way, so typing "board" finds FOB.
 *
 * The rule NAMES stay English here and are not translated. They are the ICC's
 * registered terms, quoted in English in Russian-language contracts as well —
 * translating "Free On Board" would produce a phrase that appears on no bill of
 * lading. `seaOnlyNote` is different: it is our sentence about the term, not the
 * term itself, so the caller passes it in from the catalogue.
 *
 * A stored value that is not one of the eleven — an older quote, or a code
 * written with its named place — is prepended so the field can render what is
 * actually saved. A Select whose value is absent from its options shows an empty
 * control, which would read as "no incoterm agreed" over a quote that has one. */
export function incotermOptions(
  { current, seaOnlyNote }: { current?: string; seaOnlyNote?: string } = {},
): { value: string; label: string; hint?: string }[] {
  const options = INCOTERMS_2020.map((term) => ({
    value: term.code,
    label: term.code,
    hint:
      seaOnlyNote && SEA_ONLY_INCOTERMS.includes(term.code)
        ? `${term.name} · ${seaOnlyNote}`
        : term.name,
  }))

  const stored = current?.trim()
  if (!stored || options.some((option) => option.value === stored)) return options

  return [{ value: stored, label: stored }, ...options]
}
